/**
 * Marketplace search gate: require a Care Pass that matches the selected shift
 * (Hourly Pass for hourly, Short/Quarterly for 12h/24h) before listing caregivers
 * near the selected patient's address.
 *
 * Resume draft may also carry hourly mid-wizard fields so Care Pass checkout can
 * return to package / pattern / slot selection:
 * hourlyPackageKey, hourlyPattern, hourlyRecurringPattern, hourlySelectedSlot,
 * hourlyAvailableSlots, hourlySlotsReady.
 */

import api from "../services/api";
import {
  getRequiredCarePassPlanIdsForShift,
  patientHasValidCarePassForShift,
  resolveCanonicalSearchShift,
} from "../config/careShiftWindows";

export const MARKETPLACE_SEARCH_RESUME_KEY = "marketplacePatientSearchResume";

/** Hourly pre-search fields persisted across Care Pass purchase. */
export const HOURLY_SEARCH_RESUME_FIELDS = [
  "hourlyPackageKey",
  "hourlyPattern",
  "hourlyRecurringPattern",
  "hourlySelectedSlot",
  "hourlyAvailableSlots",
  "hourlyMatchmakerMatches",
  "hourlySlotsReady",
  "hourlyDaysServiceRequired",
  "hourlySelectedDates",
  "resumeAction",
];

export function pickHourlySearchResumeFields(source = {}) {
  if (!source || typeof source !== "object") return {};
  const out = {};
  for (const key of HOURLY_SEARCH_RESUME_FIELDS) {
    if (source[key] !== undefined) out[key] = source[key];
  }
  return out;
}

/**
 * Unwrap GET /patients/:id/subscription-status envelopes to the status DTO
 * (active/planId/planCode), not the nested DB subscription row.
 */
export function unwrapPatientSubscriptionStatus(res) {
  if (!res || typeof res !== "object") return null;
  const candidates = [res.subscription, res.data?.subscription, res.data, res];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    // Status DTO: has active + (plan fields and/or status/patientId). Prefer over nested row-only.
    const hasStatusShape =
      "active" in candidate &&
      (candidate.planId != null ||
        candidate.planCode != null ||
        candidate.status != null ||
        candidate.patientId != null ||
        candidate.careDaysRemaining != null ||
        candidate.hasEverPaid != null ||
        Array.isArray(candidate.passes));
    if (hasStatusShape) {
      // Prefer the richer DTO when a nested DB row also matches loosely.
      if (Array.isArray(candidate.passes) || candidate.planId != null || candidate.planCode != null) {
        return candidate;
      }
      return candidate;
    }
  }
  // Fallback: first object that exposes active.
  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object" && "active" in candidate) return candidate;
  }
  return null;
}

export async function getPatientCarePassForShift(patientId, searchShift, bookingDays = null) {
  const id = String(patientId || "").trim();
  if (!id) return { subscription: null, passOk: false };
  try {
    const res = await api.getPatientSubscriptionStatus(id);
    const subscription = unwrapPatientSubscriptionStatus(res);
    const passOk = patientHasValidCarePassForShift(subscription, searchShift, bookingDays);
    return { subscription, passOk };
  } catch (err) {
    if (err?.code === "PATIENT_NOT_FOUND" || err?.status === 404) {
      return { subscription: null, passOk: false };
    }
    return { subscription: { active: false, status: "NONE" }, passOk: false };
  }
}

export function preferredCarePassPlanIdForShift(searchShift, bookingDays = null) {
  const allowed = getRequiredCarePassPlanIdsForShift(searchShift, { bookingDays });
  if (allowed.includes("hourly")) return "hourly";
  if (allowed.includes("short")) return "short";
  if (allowed.includes("long")) return "long";
  return allowed[0] || "hourly";
}

export function carePassMissingMessage(searchShift, bookingDays = null) {
  const shift = resolveCanonicalSearchShift(searchShift);
  const days = Number(bookingDays);
  const needsQuarterly = Number.isFinite(days) && days > 30;
  if (shift === "hourly") {
    if (Number.isFinite(days) && days > 1) {
      return `You have selected ${days} recurring booking${days === 1 ? "" : "s"}. Please select a Care Pass that covers your booking requirement.`;
    }
    return "Hourly booking requires an Hourly Pass for this patient. Buy a pass to see nearby caregivers.";
  }
  if (needsQuarterly) {
    return "Continuous care longer than one month requires a Quarterly Care Pass for this patient.";
  }
  if (Number.isFinite(days) && days > 0) {
    return `This booking needs ${days} caregiver day${days === 1 ? "" : "s"}. Choose a Care Pass that covers the full duration.`;
  }
  return "Buy a Short-term or Quarterly Care Pass for this patient to see nearby caregivers.";
}

export function writeMarketplaceSearchResume(payload) {
  try {
    sessionStorage.setItem(MARKETPLACE_SEARCH_RESUME_KEY, JSON.stringify(payload || {}));
  } catch {
    /* ignore */
  }
}

export function readMarketplaceSearchResume() {
  try {
    const raw = sessionStorage.getItem(MARKETPLACE_SEARCH_RESUME_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearMarketplaceSearchResume() {
  try {
    sessionStorage.removeItem(MARKETPLACE_SEARCH_RESUME_KEY);
  } catch {
    /* ignore */
  }
}

/** Resolve a patient id from mixed API / UI shapes. */
export function resolvePatientRecordId(...candidates) {
  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    if (typeof candidate === "object") {
      const fromObj = String(candidate.patientId || candidate.patient_id || "").trim();
      if (fromObj) return fromObj;
      continue;
    }
    const raw = String(candidate).trim();
    if (raw) return raw;
  }
  return "";
}

/**
 * Send the customer to Care Pass pricing, preserving marketplace search resume context.
 */
export function navigateToCarePassForMarketplaceSearch(
  navigate,
  { patient, searchShift, searchContext = {}, from = "/marketplace", bookingDays = null } = {}
) {
  const patientId = patient?.patientId || searchContext?.patientId;
  const planId = preferredCarePassPlanIdForShift(searchShift, bookingDays);
  writeMarketplaceSearchResume({
    patientId,
    patient,
    searchShift,
    from,
    ...pickHourlySearchResumeFields(searchContext),
    ...searchContext,
  });
  const params = new URLSearchParams();
  if (patientId) params.set("patientId", String(patientId));
  params.set("plan", planId);
  if (searchShift) params.set("shift", String(searchShift));
  params.set("resumeMarketplaceSearch", "1");
  navigate(`/pricing?${params.toString()}#pricing-plans`, {
    state: {
      from,
      patientId: patientId || undefined,
      resumeMarketplaceSearch: true,
      searchShift: searchShift || undefined,
      marketplaceSearchPrefill: searchContext?.marketplaceSearchPrefill || null,
      activePatient: patient || null,
    },
  });
  return { planId };
}
